import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { GUIDE, FIRST_STEPS } from "@/lib/guide";

const Steps = () => (
  <ol className="mt-6 space-y-3 stagger">
    {FIRST_STEPS.map((s, i) => (
      <li key={s.title} data-testid={`welcome-step-${i}`} className="flex gap-4 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
        <span className="w-8 h-8 shrink-0 rounded-full bg-gold text-noir text-sm font-bold flex items-center justify-center">{i + 1}</span>
        <div><p className="text-sm font-semibold text-white">{s.title}</p><p className="text-xs text-warm-text mt-1 leading-relaxed">{s.text}</p></div>
      </li>
    ))}
  </ol>
);

const Tour = () => (
  <div className="mt-6 grid gap-3 sm:grid-cols-2 stagger">
    {GUIDE.map((g) => (
      <div key={g.id} data-testid={`welcome-menu-${g.id}`} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
        <div className="flex items-center gap-2.5"><g.icon className="w-4 h-4 text-gold" strokeWidth={1.5} /><p className="text-sm font-semibold">{g.title}</p></div>
        <p className="text-xs text-warm-text mt-1.5 leading-relaxed">{g.intro}</p>
      </div>
    ))}
  </div>
);

export function WelcomeDialog({ onDone }) {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const finish = async (path) => {
    setUser({ ...user, welcome_seen: true });
    onDone();
    api.post("/me/welcome-seen").catch(() => {});
    if (path) nav(path);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && finish()}>
      <DialogContent data-testid="welcome-dialog" className="bg-ivory-card rounded-2xl border-gold/30 w-[calc(100%-1.5rem)] max-w-xl max-h-[88vh] overflow-y-auto">
        <img src="/brand/monogram-oro.png" alt="" className="h-8 w-auto self-start" />
        <p className="eyebrow mt-2">{step === 0 ? "Benvenuta" : "Le sezioni dell'app"}</p>
        <DialogTitle className="font-serif text-3xl font-light leading-tight">
          {step === 0 ? <>Ciao <em className="text-gold-gradient">{user.center?.center_name}</em>, iniziamo?</> : "Cosa trovi in My Skin Lovers"}
        </DialogTitle>
        <DialogDescription className="text-warm-text">
          {step === 0 ? "Ecco i primi passi per sfruttare al meglio la tua area riservata." : "Puoi rivedere la guida completa in qualsiasi momento dall'icona ? in alto."}
        </DialogDescription>
        {step === 0 ? <Steps /> : <Tour />}
        <div className="sticky -bottom-6 -mx-6 -mb-6 px-6 py-4 bg-ivory-card/95 backdrop-blur-md border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1.5">{[0, 1].map((i) => <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-6 bg-gold" : "w-1.5 bg-white/20"}`} />)}</div>
          {step === 0
            ? <button data-testid="welcome-next-btn" onClick={() => setStep(1)} className="btn-gold">Avanti <ArrowRight className="w-4 h-4" /></button>
            : <div className="flex flex-wrap gap-3">
                <button data-testid="welcome-guide-btn" onClick={() => finish("/guida")} className="btn-ghost">Guida completa</button>
                <button data-testid="welcome-done-btn" onClick={() => finish()} className="btn-gold"><Check className="w-4 h-4" /> Inizia</button>
              </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NewsDialog() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/news/unseen").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  const n = items[0];
  if (!n) return null;
  const next = (path) => {
    api.post(`/news/${n.id}/seen`).catch(() => {});
    setItems(items.slice(1));
    if (path) nav(path);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && next()}>
      <DialogContent data-testid="news-dialog" className="bg-ivory-card rounded-2xl border-gold/30 w-[calc(100%-1.5rem)] max-w-lg max-h-[88vh] overflow-y-auto">
        <span className="w-12 h-12 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center shadow-[0_0_30px_-6px_rgba(212,175,55,0.7)]">
          <Sparkles className="w-5 h-5 text-gold" strokeWidth={1.5} />
        </span>
        <p className="eyebrow mt-2">Novità{items.length > 1 ? ` · 1 di ${items.length}` : ""}</p>
        <DialogTitle data-testid="news-dialog-title" className="font-serif text-3xl font-light leading-tight">{n.title}</DialogTitle>
        <DialogDescription data-testid="news-dialog-body" className="text-sm text-[#E8E4DC] leading-relaxed whitespace-pre-wrap">{n.body}</DialogDescription>
        <div className="mt-4 flex flex-wrap justify-end gap-3">
          <button data-testid="news-dialog-ok" onClick={() => next()} className={n.link_path ? "btn-ghost" : "btn-gold"}>Ho capito</button>
          {n.link_path && <button data-testid="news-dialog-try" onClick={() => next(n.link_path)} className="btn-gold">Provala ora <ArrowRight className="w-4 h-4" /></button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CenterIntro() {
  const { user } = useAuth();
  const [welcomeOpen, setWelcomeOpen] = useState(!user.welcome_seen);
  return welcomeOpen ? <WelcomeDialog onDone={() => setWelcomeOpen(false)} /> : <NewsDialog />;
}
